/** A single cell in the 42-cell (6×7) calendar grid. */
export interface CalendarDay {
  readonly iso: string;          // "YYYY-MM-DD" in local time
  readonly day: number;          // day-of-month (1-31)
  readonly isCurrentMonth: boolean;
  readonly isToday: boolean;
}

/**
 * Format a local Date as "YYYY-MM-DD" without any timezone conversion.
 * Uses local year/month/day — never converts to UTC.
 */
export const toIso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

/**
 * Build a Monday-first 42-cell grid for the given year + 1-indexed month.
 * Padding cells for the previous/next month are included with `isCurrentMonth === false`.
 */
export const buildMonthGrid = (year: number, month: number): CalendarDay[] => {
  const todayIso = toIso(new Date());

  // First day of the target month
  const firstOfMonth = new Date(year, month - 1, 1);
  // Raw getDay(): 0=Sun, 1=Mon, …, 6=Sat → convert to Monday-first (0=Mon, …, 6=Sun)
  const rawDow = firstOfMonth.getDay();
  const leadingDays = rawDow === 0 ? 6 : rawDow - 1;

  // Last day of the target month
  const lastOfMonth = new Date(year, month, 0).getDate();

  const cells: CalendarDay[] = [];

  // Leading padding days (from previous month)
  for (let i = leadingDays - 1; i >= 0; i--) {
    const d = new Date(year, month - 1, -i); // day 0 = last day of prev month, -1 = second-to-last, etc.
    const iso = toIso(d);
    cells.push({ iso, day: d.getDate(), isCurrentMonth: false, isToday: iso === todayIso });
  }

  // Current month days
  for (let d = 1; d <= lastOfMonth; d++) {
    const date = new Date(year, month - 1, d);
    const iso = toIso(date);
    cells.push({ iso, day: d, isCurrentMonth: true, isToday: iso === todayIso });
  }

  // Trailing padding days (into next month)
  const trailingDays = 42 - cells.length;
  for (let d = 1; d <= trailingDays; d++) {
    const date = new Date(year, month, d); // month (0-indexed) = first day of next month
    const iso = toIso(date);
    cells.push({ iso, day: d, isCurrentMonth: false, isToday: iso === todayIso });
  }

  return cells;
};
